<script>
    import { onMount } from 'svelte';
    import { storyStore } from '../stores/storyStore.js';
    import { get } from 'svelte/store';
    import { shareStory } from '../services/exportService.js';

    export let isOpen = false;
    export let onClose;

    let slides = [];
    let renderedSlides = [];
    let currentSlideIndex = 0;
    let hasContent = false;
    let slideTitle = '';
    let jsPDF;
    let isLoading = false;
    let loadingMessage = '';
    let errorMessage = '';

    onMount(async () => {
        jsPDF = window.jspdf.jsPDF;

        const currentStory = get(storyStore);
        slides = currentStory.slides;

        // Check if any slide has content
        hasContent = slides.some(slide => slide && ((slide.canvasState && slide.canvasState.length > 0) || slide.script));

        // Set loading state while rendering slides
        if (hasContent) {
            isLoading = true;
            loadingMessage = 'Preparing slides...';
            errorMessage = '';

            try {
                // Track progress for better user feedback
                let completedSlides = 0;
                const totalSlides = slides.length;

                // Render each slide with progress tracking
                renderedSlides = await Promise.all(slides.map(async (slide, index) => {
                    if (slide) {
                        loadingMessage = `Rendering slide ${index + 1} of ${totalSlides}...`;
                        const dataUrl = await renderCanvas(slide);
                        
                        // Update progress
                        completedSlides++;
                        loadingMessage = `Rendered ${completedSlides} of ${totalSlides} slides`;
                        
                        return { ...slide, dataUrl };
                    }
                    return slide;
                }));

                // Loading complete
                isLoading = false;
                loadingMessage = '';
            } catch (error) {
                console.error('Error rendering slides:', error);
                errorMessage = `Error preparing slides: ${error.message}`;
                isLoading = false;
            }
        }

        // Set the initial slide title
        updateSlideTitle();
    });

    function closeModal() {
        if (onClose) {
            onClose();
        }
    }

    function nextSlide() {
        if (currentSlideIndex < renderedSlides.length - 1) {
            currentSlideIndex++;
            updateSlideTitle();
        }
    }

    function prevSlide() {
        if (currentSlideIndex > 0) {
            currentSlideIndex--;
            updateSlideTitle();
        }
    }

    function updateSlideTitle() {
        if (slides[currentSlideIndex]) {
            slideTitle = slides[currentSlideIndex].title || `Slide ${currentSlideIndex + 1}`;
        }
    }

    async function renderCanvas(slide) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = 375;
        tempCanvas.height = 667;
        const fabricCanvas = new window.fabric.Canvas(tempCanvas);

        if (slide.canvasState && slide.canvasState.length > 0) {
            try {
                // Pre-process canvas state to handle clipboard images
                const processedState = slide.canvasState.map(obj => {
                    // Create a new object to avoid modifying the original
                    const newObj = {...obj};
                    
                    // Check if this is a clipboard image that needs special handling
                    if (newObj.type === 'image' && 
                        newObj.metadata && 
                        newObj.metadata.isClipboardImage) {
                        
                        console.log('Processing clipboard image for PDF export');
                        
                        // If the src is a valid data URL, ensure it's not truncated/compressed
                        if (typeof newObj.src === 'string' && newObj.src.startsWith('data:image')) {
                            // Check if the src looks truncated or corrupted
                            if (newObj.src.includes('compressed') || newObj.src.length < 1000) {
                                console.log('Data URL appears to be truncated or corrupted');
                                // Try to get the embedded data URL from metadata if available
                                if (newObj.metadata && newObj.metadata.embeddedDataUrl) {
                                    console.log('Using embedded data URL from metadata');
                                    newObj.src = newObj.metadata.embeddedDataUrl;
                                } else {
                                    console.log('No valid data URL available, creating placeholder');
                                    // Replace with a colored rectangle as placeholder
                                    newObj.type = 'rect';
                                    newObj.fill = '#e2e8f0';
                                    newObj.stroke = '#cbd5e1';
                                    newObj.strokeWidth = 2;
                                    delete newObj.src;
                                }
                            } else {
                                console.log('Using existing data URL for clipboard image');
                            }
                        } 
                        // If the src is a blob URL or missing, create a placeholder
                        else if (typeof newObj.src === 'string' && newObj.src.startsWith('blob:')) {
                            console.log('Replacing blob URL with placeholder', newObj.src);
                            
                            // Try to use embedded data URL first if available
                            if (newObj.metadata && newObj.metadata.embeddedDataUrl) {
                                console.log('Using embedded data URL from metadata instead of blob URL');
                                newObj.src = newObj.metadata.embeddedDataUrl;
                            } else {
                                // Replace with a colored rectangle as placeholder
                                newObj.type = 'rect';
                                newObj.fill = '#e2e8f0';
                                newObj.stroke = '#cbd5e1';
                                newObj.strokeWidth = 2;
                                delete newObj.src;
                            }
                        }
                    }
                    
                    return newObj;
                });

                await new Promise((resolve) => {
                    fabricCanvas.loadFromJSON(
                        { version: '5.2.4', objects: processedState },
                        () => {
                            // Add a white background
                            const backgroundRect = new window.fabric.Rect({
                                left: 0,
                                top: 0,
                                width: fabricCanvas.width,
                                height: fabricCanvas.height,
                                fill: 'white',
                                selectable: false,
                                evented: false,
                            });

                            // Add the background rectangle to the canvas
                            fabricCanvas.add(backgroundRect);
                            fabricCanvas.sendToBack(backgroundRect); // Ensure it is behind all other elements

                            fabricCanvas.renderAll();
                            resolve();
                        }
                    );
                });
            } catch (error) {
                console.error('Error rendering canvas for PDF:', error);
                
                // If there's an error, create a fallback white canvas with error message
                const ctx = tempCanvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                ctx.fillStyle = 'red';
                ctx.font = '14px Arial';
                ctx.fillText('Error rendering slide content', 20, 50);
                ctx.font = '12px Arial';
                ctx.fillText(error.message || 'Unknown error', 20, 70);
            }
        } else {
            // If no canvas state, create a blank white canvas
            const ctx = tempCanvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
        }

        return tempCanvas.toDataURL('image/png');
    }

    async function handleDownloadPDF() {
        if (!jsPDF) {
            errorMessage = 'PDF library not loaded. Please refresh and try again.';
            return;
        }
        
        try {
            isLoading = true;
            loadingMessage = 'Generating PDF...';
            errorMessage = '';
            
            // Use the progress callback with the updated shareStory function
            await shareStory(jsPDF, (message, percent) => {
                loadingMessage = message;
                // You could also use percent to update a progress bar if desired
                console.log(`PDF Export Progress: ${percent}% - ${message}`);
            });
            
            isLoading = false;
            loadingMessage = '';
        } catch (error) {
            console.error('Error generating PDF:', error);
            errorMessage = `Error generating PDF: ${error.message}`;
            isLoading = false;
        }
    }
</script>

{#if isOpen}
    <div class="modal-overlay" on:click={closeModal}>
        <div class="modal-content" on:click|stopPropagation>
            <button class="close-button" on:click={closeModal}>×</button>
            <h2>PDF Preview</h2>
            
            {#if errorMessage}
                <div class="error-message">
                    <p>{errorMessage}</p>
                    <button on:click={() => errorMessage = ''}>Dismiss</button>
                </div>
            {/if}
            
            {#if isLoading}
                <div class="loading-overlay">
                    <div class="spinner"></div>
                    <p>{loadingMessage}</p>
                </div>
            {:else if hasContent}
                <div class="slide-header">
                    <button class="nav-arrow" on:click={prevSlide} disabled={currentSlideIndex === 0}>&larr;</button>
                    <h3 class="title-display">{slideTitle}</h3>
                    <button class="nav-arrow" on:click={nextSlide} disabled={currentSlideIndex === renderedSlides.length - 1}>&rarr;</button>
                </div>
                {#if renderedSlides.length > 0}
                    <div class="slide-container">
                        <div class="slide-image">
                            {#if renderedSlides[currentSlideIndex]}
                                <img src={renderedSlides[currentSlideIndex].dataUrl} alt="Slide Image" />
                            {/if}
                        </div>
                        <div class="slide-script">
                            {#if renderedSlides[currentSlideIndex] && renderedSlides[currentSlideIndex].script}
                                <p>{renderedSlides[currentSlideIndex].script}</p>
                            {/if}
                        </div>
                    </div>
                {/if}
                <div class="button-container">
                    <button class="download-button" on:click={handleDownloadPDF} disabled={isLoading}>
                        {isLoading ? 'Generating...' : 'Download PDF'}
                    </button>
                </div>
            {:else}
                <p>Please add content to your canvas or script before exporting a PDF.</p>
            {/if}
        </div>
    </div>
{/if}

<style>

.modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
    }

    .modal-content {
        background: white;
        padding: 20px;
        border-radius: 8px;
        width: 80%;
        max-width: 1000px;
        height: 90vh;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        overflow-y: auto; /* Allows scrolling if content is too long */
    }

    .close-button {
        position: absolute;
        top: 10px;
        right: 10px;
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
    }

    .slide-navigation {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
    }

    .slide-navigation button {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
    }

    .slide-container {
        flex-grow: 1;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        overflow-y: auto;
    }

    .slide-image {
        flex: 1;
        margin-right: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-right: 1px solid #ccc; /* Add vertical line */
        padding-right: 20px; /* Add spacing between the line and content */
    }

    .slide-image img {
        max-width: 56%;
        height: auto;
        display: block;
    }

    .slide-script {
        flex: 1;
        max-height: 300px;
        overflow-y: auto;
        display: flex;
        align-items: flex-start;
        padding-left: 20px; /* Add spacing between the line and script */
    }

    .slide-script p {
        margin: 0;
    }

    .button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: auto; /* Pushes it to the bottom */
        padding-top: 10px;
        padding-bottom: 10px; /* Ensures spacing at bottom */
        background: white; /* Ensures visibility if modal scrolls */
    }

    .generate-button {
        background: #007bff;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
    }

    .volume-button {
        background: none;
        border: none;
        cursor: pointer;
        margin-right: 10px;
    }

    .volume-button img {
        width: 24px;
        height: 24px;
    }

    .slide-title {
        margin-bottom: 20px;
        text-align: center;
    }

    .title-input {
        font-size: 1.2rem;
        font-weight: bold;
        text-align: center;
        border: 1px solid #ccc;
        border-radius: 4px;
        padding: 5px;
        width: 80%;
    }

    .edit-icon {
        font-size: 1rem;
        color: #888;
    }

    .edit-icon:hover {
        color: #555;
    }

    .slide-header {
        display: flex;
        align-items: center;
        justify-content: center; /* Center the title and arrows */
        gap: 10px; /* Add spacing between the arrows and the title */
        margin-bottom: 20px;
    }

    .nav-arrow {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        padding: 0 5px; /* Reduce padding to bring arrows closer to the title */
    }

    .nav-arrow:disabled {
        color: #ccc;
        cursor: not-allowed;
    }

    .title-display {
        font-size: 1.5rem;
        font-weight: bold;
        text-align: center;
    }
    .download-button {
        background: #007bff;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        cursor: pointer;
    }

    .download-button:hover {
        background: #0056b3;
    }

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 10;
    }
    
    .spinner {
        width: 40px;
        height: 40px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin-bottom: 20px;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    .error-message {
        background: #fee2e2;
        color: #dc2626;
        padding: 10px 15px;
        border-radius: 6px;
        margin-bottom: 15px;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    
    .error-message button {
        background: #dc2626;
        color: white;
        border: none;
        padding: 4px 8px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
    }
    
    .download-button:disabled {
        background: #94a3b8;
        cursor: not-allowed;
    }
</style>